import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tripsApi } from '../../api/trips.api';
import type { SharedTrip, TripShareSection } from '../../types';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { Icon, MapPin, CalendarDays, BedDouble, Users, Globe } from '../../components/ui/Icon';
import { getDestColor } from './shared.ui';
import SharedItinerary from './SharedItinerary';
import SharedPlaces from './SharedPlaces';
import SharedBookings from './SharedBookings';
import SharedTips from './SharedTips';
import SharedPhotos from './SharedPhotos';
import SharedMap from './SharedMap';
import SharedDiary from './SharedDiary';
import FollowBox from './FollowBox';
import TripSummaryStrip from '../../components/stats/TripSummaryStrip';
import { followersApi } from '../../api/followers.api';
import { toast } from '../../components/ui/Toast';

/**
 * A trip as somebody without an account sees it.
 *
 * The creator picks what this shows; `trip.sections` is that list as the server
 * resolved it. The page only ever renders what actually arrived — it is not the
 * thing enforcing the choice. What an anonymous visitor may reach is decided in
 * `server/src/modules/trips/sharedTrip.service.ts`, and it is decided there
 * because the last time this page was the only filter, booking documents were
 * shipping to anyone with the link for months without a single pixel on screen.
 */

type Tab = 'diary' | 'itinerary' | 'places' | 'map' | 'bookings' | 'tips' | 'photos';

export default function SharedTripPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [trip, setTrip] = useState<SharedTrip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState<Tab>('itinerary');
  const [params, setParams] = useSearchParams();

  // ADR-0044: the links in the follower emails land here.
  useEffect(() => {
    const confirm = params.get('confirm');
    const unsubscribe = params.get('unsubscribe');
    if (!confirm && !unsubscribe) return;
    const call = confirm ? followersApi.confirmEmail(confirm) : followersApi.unsubscribeEmail(unsubscribe!);
    call
      .then(() => toast(t(confirm ? 'shared.follow.confirmed' : 'shared.follow.unsubscribed'), 'success'))
      .catch(() => toast(t('shared.follow.linkExpired'), 'error'))
      .finally(() => setParams({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    tripsApi.getShared(token!)
      // A follower arriving from a diary email wants the diary first.
      .then(tr => { setTrip(tr); if (tr.sections.includes('DIARY')) setTab('diary'); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const tabs = useMemo(() => {
    if (!trip) return [];
    const has = (s: TripShareSection) => trip.sections.includes(s);
    const bookingCount = (trip.flights?.length ?? 0) + (trip.hotels?.length ?? 0) + (trip.bookings?.length ?? 0);
    return ([
      { key: 'diary'     as Tab, label: t('shared.sections.diary'),     show: has('DIARY'), count: trip.diary?.length },
      { key: 'itinerary' as Tab, label: t('shared.sections.itinerary'), show: has('ITINERARY') && (trip.days?.length ?? 0) > 0 },
      { key: 'places'    as Tab, label: t('shared.sections.places'),    show: has('PLACES') && (trip.venues?.length ?? 0) > 0, count: trip.venues?.length },
      { key: 'map'       as Tab, label: t('shared.sections.map'),       show: has('MAP') },
      { key: 'bookings'  as Tab, label: t('shared.sections.travel'),    show: has('BOOKINGS') && bookingCount > 0 },
      { key: 'tips'      as Tab, label: t('shared.sections.tips'),      show: has('TIPS') && (trip.tips?.length ?? 0) > 0, count: trip.tips?.length },
      { key: 'photos'    as Tab, label: t('shared.sections.photos'),    show: has('PHOTOS') && (trip.photos?.length ?? 0) > 0, count: trip.photos?.length },
    ]).filter(x => x.show);
  }, [trip, t]);

  // Land on a tab that exists: a trip shared as places-only has no itinerary.
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some(x => x.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab]);

  if (loading) return <LoadingScreen fullscreen />;

  if (error || !trip) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50 dark:bg-slate-900 p-8 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Icon as={Globe} size={24} className="text-slate-400" />
      </div>
      <h1 className="font-display font-bold text-2xl text-slate-800 dark:text-slate-100">{t('shared.notFoundTitle')}</h1>
      <p className="text-slate-500 max-w-sm">{t('shared.notFoundDesc')}</p>
    </div>
  );

  const destColor = getDestColor(trip.destination);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div
        className="relative py-12 px-6 text-white overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${destColor}ee, ${destColor})` }}
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px), radial-gradient(circle at 20% 80%, white 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="flex items-center gap-1.5 text-white/70 text-xs mb-3">
            <Icon as={Globe} size={14} />
            {t('shared.viewOnly')}
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl mb-2">{trip.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1.5">
              <Icon as={MapPin} size={16} />
              {trip.destination}
            </span>
            <span className="flex items-center gap-1.5">
              <Icon as={CalendarDays} size={16} />
              {format(new Date(trip.startDate), 'MMM d')} – {format(new Date(trip.endDate), 'MMM d, yyyy')}
            </span>
            <span className="flex items-center gap-1.5">
              <Icon as={BedDouble} size={16} />
              {t('shared.nights', { count: trip.nights })}
            </span>
            <span className="flex items-center gap-1.5">
              <Icon as={Users} size={16} />
              {t('shared.traveller', { count: trip.memberCount })}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {trip.notes && (
          <section className="card p-5">
            <h2 className="font-semibold text-sm text-slate-700 dark:text-slate-300 mb-2">{t('shared.sections.about')}</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap break-words">{trip.notes}</p>
          </section>
        )}

        {tabs.length > 1 && (
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none border-b border-slate-200 dark:border-slate-800">
            {tabs.map(item => (
              <button
                key={item.key}
                onClick={() => setTab(item.key)}
                className={`shrink-0 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
                  tab === item.key
                    ? 'border-teal-500 text-slate-900 dark:text-slate-100'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
                }`}
              >
                {item.label}
                {item.count != null && <span className="ml-1.5 text-2xs text-slate-400">{item.count}</span>}
              </button>
            ))}
          </div>
        )}

        {trip.sections.includes('DIARY') && <FollowBox shareToken={token!} />}

        {tab === 'diary' && trip.summary && <TripSummaryStrip summary={trip.summary} />}
        {tab === 'diary'     && <SharedDiary entries={trip.diary ?? []} />}
        {tab === 'itinerary' && <SharedItinerary days={trip.days ?? []} meals={trip.meals} />}
        {tab === 'places'    && <SharedPlaces venues={trip.venues ?? []} />}
        {tab === 'map'       && <SharedMap trip={trip} token={token!} />}
        {tab === 'bookings'  && (
          <SharedBookings flights={trip.flights ?? []} hotels={trip.hotels ?? []} bookings={trip.bookings ?? []} />
        )}
        {tab === 'tips'      && <SharedTips tips={trip.tips ?? []} />}
        {tab === 'photos'    && <SharedPhotos photos={trip.photos ?? []} />}

        <p className="text-center text-xs text-slate-400 pt-4 pb-8">{t('shared.footer')}</p>
      </div>
    </div>
  );
}
